/* Reusable public-style preview of a sale listing.
   Used in the particulier wizard "controleer" step AND the admin preview page
   so what the seller sees == what the admin sees == what visitors see after approval. */
import React, { useState } from "react";
import {
  MapPin, BedDouble, Maximize, Trees, Calendar, Home as HomeIcon,
  Ruler, Layers, Building2, Euro, ShieldCheck, Landmark, Phone, Mail,
} from "lucide-react";
import { PrivateSellerNotice } from "./PrivateSellerNotice";

const Fact = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-2 rounded-xl border border-border px-3 py-2.5">
    <Icon size={16} className="shrink-0 text-brandblue" />
    <div className="min-w-0">
      <div className="text-[11px] font-semibold uppercase text-slate-400">{label}</div>
      <div className="truncate text-sm font-semibold text-navy">{value}</div>
    </div>
  </div>
);

const priceLabel = (price, cond) => {
  if (!price) return "Prijs op aanvraag";
  const n = Number(price).toLocaleString("nl-NL");
  const suffix = cond === "von" ? " v.o.n." : cond === "kk" ? " k.k." : "";
  return `€ ${n}${suffix}`;
};

/* Props: data = raw/public listing shape ({title,street,...,gallery,...}),
         showContact = true to render the "Bied/Contact" sidebar (used in preview). */
export const ListingPreview = ({ data, showContact = true }) => {
  const [activeImg, setActiveImg] = useState(0);
  if (!data) return null;
  const gallery = (data.gallery && data.gallery.length ? data.gallery : (data.image ? [data.image] : []));
  const mapSrc =
    data.lat && data.lng
      ? `https://www.openstreetmap.org/export/embed.html?bbox=${data.lng - 0.01}%2C${data.lat - 0.006}%2C${data.lng + 0.01}%2C${data.lat + 0.006}&layer=mapnik&marker=${data.lat}%2C${data.lng}`
      : null;
  const label = priceLabel(data.price, data.price_condition);

  return (
    <div className="mx-auto max-w-5xl">
      <div className="overflow-hidden rounded-3xl border border-border bg-slate-100">
        <div className="relative aspect-[16/9] w-full bg-slate-200">
          {gallery[activeImg] ? (
            <img src={gallery[activeImg]} alt={data.title || data.street} className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full items-center justify-center text-slate-300"><HomeIcon size={56} /></div>
          )}
          {data.seller_type === "particulier" && (
            <span className="absolute left-4 top-4 rounded-full bg-amber-500 px-3 py-1 text-xs font-bold text-white">Particulier</span>
          )}
        </div>
        {gallery.length > 1 && (
          <div className="flex gap-2 overflow-x-auto p-3">
            {gallery.slice(0, 12).map((g, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setActiveImg(i)}
                className={`h-16 w-24 shrink-0 overflow-hidden rounded-lg border-2 ${i === activeImg ? "border-brandblue" : "border-transparent"}`}
              >
                <img src={g} alt="" className="h-full w-full object-cover" />
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="mt-6 grid gap-8 lg:grid-cols-[1fr_340px]">
        <div>
          <div>
            <h1 className="font-display text-3xl font-extrabold text-navy">
              {data.title || `${data.property_type || "Woning"} in ${data.city || ""}`}
            </h1>
            <p className="mt-1 flex items-center gap-1 text-slate-500">
              <MapPin size={15} />{" "}
              {[data.street, data.house_number, data.house_number_addition].filter(Boolean).join(" ")},{" "}
              {data.postcode} {data.city}
            </p>
            <PrivateSellerNotice listing={data} className="mt-2" />
          </div>

          <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3">
            {data.m2 ? <Fact icon={Maximize} label="Woonopp." value={`${data.m2} m²`} /> : null}
            {data.plot_m2 ? <Fact icon={Trees} label="Perceel" value={`${data.plot_m2} m²`} /> : null}
            {data.volume_m3 ? <Fact icon={Layers} label="Inhoud" value={`${data.volume_m3} m³`} /> : null}
            {data.bedrooms ? <Fact icon={BedDouble} label="Slaapkamers" value={data.bedrooms} /> : null}
            {data.rooms ? <Fact icon={Building2} label="Kamers" value={data.rooms} /> : null}
            {data.year_built ? <Fact icon={Calendar} label="Bouwjaar" value={data.year_built} /> : null}
            {data.energy_label && data.energy_label !== "—" ? (
              <Fact icon={Ruler} label="Energielabel" value={data.energy_label} />
            ) : null}
            {data.property_type ? <Fact icon={HomeIcon} label="Type" value={data.property_type} /> : null}
          </div>

          {data.description && (
            <div className="mt-8">
              <h2 className="font-display text-xl font-bold text-navy">Omschrijving</h2>
              <p className="mt-3 whitespace-pre-line text-slate-600">{data.description}</p>
            </div>
          )}

          {data.features && data.features.length > 0 && (
            <div className="mt-8">
              <h2 className="font-display text-xl font-bold text-navy">Kenmerken</h2>
              <div className="mt-3 flex flex-wrap gap-2">
                {data.features.map((ft, i) => (
                  <span key={i} className="rounded-full bg-slate-50 px-3 py-1.5 text-sm text-slate-600">{ft}</span>
                ))}
              </div>
            </div>
          )}

          {(data.ownership || data.vve_contribution || data.service_costs || data.monument) && (
            <div className="mt-8">
              <h2 className="font-display text-xl font-bold text-navy">Juridisch & financieel</h2>
              <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
                {data.ownership ? <Fact icon={ShieldCheck} label="Eigendom" value={data.ownership} /> : null}
                {data.vve_contribution ? <Fact icon={Euro} label="VvE p/m" value={`€ ${data.vve_contribution}`} /> : null}
                {data.service_costs ? <Fact icon={Euro} label="Servicekosten" value={`€ ${data.service_costs}`} /> : null}
                {data.monument ? <Fact icon={Landmark} label="Monument" value="Ja" /> : null}
              </div>
            </div>
          )}

          {mapSrc && (
            <div className="mt-8">
              <h2 className="font-display text-xl font-bold text-navy">Locatie</h2>
              <div className="mt-3 overflow-hidden rounded-2xl border border-border">
                <iframe title="kaart" src={mapSrc} className="h-64 w-full" loading="lazy" />
              </div>
            </div>
          )}
        </div>

        {showContact && (
          <aside>
            <div className="sticky top-24 space-y-4 rounded-3xl border border-border bg-white p-6 shadow-sm">
              <div className="font-display text-3xl font-extrabold text-navy">{label}</div>
              <div className="text-sm text-slate-400">
                {data.seller_type === "particulier" ? "Van particulier" : data.agency_name || "Makelaar"}
              </div>
              {data.seller_name && (
                <div className="rounded-2xl bg-slate-50 p-3 text-sm text-slate-600">
                  <div className="font-semibold text-navy">{data.seller_name}</div>
                  {data.seller_email && (
                    <div className="mt-1 flex items-center gap-1.5"><Mail size={13} /> {data.seller_email}</div>
                  )}
                  {data.seller_phone && (
                    <div className="mt-1 flex items-center gap-1.5"><Phone size={13} /> {data.seller_phone}</div>
                  )}
                </div>
              )}
              {data.available_from && (
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                  Beschikbaar: <span className="font-bold text-navy">{data.available_from}</span>
                </div>
              )}
              <button
                type="button"
                disabled
                className="w-full cursor-not-allowed rounded-xl bg-brandblue py-3 text-sm font-bold text-white opacity-80"
              >
                Contact opnemen
              </button>
              <p className="text-center text-xs text-slate-400">
                Zo zien bezoekers de advertentie na goedkeuring.
              </p>
            </div>
          </aside>
        )}
      </div>
    </div>
  );
};

export default ListingPreview;
